import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  fetchItems,
  fetchItem,
  fetchSum,
  fetchSuitableSpots,
  searchRefHosts,
  exportToSpotsList
} from '@/apps/traforama/api/marketplaceStat'

import {
  IMarketplaceStat,
  IMarketplaceStatPayload,
  IMarketplaceStatFilter
} from '@/apps/traforama/interfaces/marketplaceStat'
import useCrudStore from '@/hooks/useCrudStore'

import { IMessage, AdType, Stream } from '@/api/types'
import { sendMessage } from '@/apps/traforama/api/websites'

const defaultFilter: IMarketplaceStatFilter = {
  type_id: AdType.Popunder,
  stream: Stream.Adult,
  traffic_qualities: [],
  query: '',
  availability: [true],
  pub_types: [],
  device_ids: [],
  countries_ids: [],
  position: [],
  banner_type: [],
  size_id: undefined
}

export const useMarketplaceStore = defineStore('marketplace', () => {
  const sum = ref<number>(0)

  const {
    items,
    currentItems,
    itemsLoading,
    listLoading,
    currentPage,
    total,
    filter,
    limit,
    SetFilters,
    SetCurrentPage,
    FetchItems,
    FetchItem,
    SetLimit,
    SetSortField,
    SetSortDirection,
    setCurrentItems,
    setTotal,
    itemById,
    payload,
    error
  } = useCrudStore({
    defaultFilter,
    fetchItems,
    fetchItem
  })

  const FetchSum = async (currentPayload?: IMarketplaceStatPayload) => {
    try {
      currentPayload = currentPayload || payload.value
      listLoading.value = true
      const response = await fetchSum(currentPayload)
      setSum(response.data)
      listLoading.value = false
    } catch {
      listLoading.value = false
    }
  }

  const SuitableSpots = async (payload: IMarketplaceStatPayload) => {
    const response = await fetchSuitableSpots(payload)
    return response.data
  }

  const SearchRefHosts = async (query: string) => {
    const response = await searchRefHosts(query)
    return response.data
  }

  const GetCsv = async (payload: IMarketplaceStatPayload) => {
    const response = await fetchItems(payload)
    return response.data.map((item) => item.attributes)
  }

  const ExportToSpotsList = async (payload: IMarketplaceStatPayload) => {
    try {
      const response = await exportToSpotsList(payload)
      return response.data
    } catch (e: any) {
      error.value = e.toString()
      throw e
    } finally {
      error.value = null
    }
  }

  const SendMessage = async (payload: IMessage) => {
    listLoading.value = true
    await sendMessage(payload)
    listLoading.value = false
  }

  const UpdateCurrentItemsByIds = (ids: string[]) => {
    const items = ids.map(
      (id) => <IMarketplaceStat>{ id: id, attributes: itemById.value(id) }
    )
    setCurrentItems(items)
    setTotal(items.length)
  }

  const setSum = (newSum: number) => {
    sum.value = newSum
  }

  return {
    // State
    items,
    currentItems,
    sum,
    itemsLoading,
    listLoading,
    currentPage,
    total,
    filter,
    limit,

    // Actions
    FetchItems,
    FetchItem,
    FetchSum,
    SuitableSpots,
    SearchRefHosts,
    GetCsv,
    SendMessage,
    UpdateCurrentItemsByIds,
    SetLimit,
    SetSortField,
    SetSortDirection,
    SetFilters,
    SetCurrentPage,
    ExportToSpotsList,

    // Getters
    itemById
  }
})
